import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from '@mui/icons-material/Delete';
import { actions } from "../../../../redux/store/store";
import { useRemoveDriverSignatureMutation, useUpdateDriverMutation } from "../../../../api/drivers";
import { useState } from "react";
import { sendSuccessMessage } from "../../../../constants/extras/sendSuccessMsg";
import { checkForError } from "../../../../constants/extras/errorHandlers";
import ConfirmDialogModal from "../confirmDialog";
import { useForm, useWatch } from "react-hook-form";
import { CircularProgress, Modal } from "@mui/material";
import moment from "moment/moment";
import { DateCalendar } from "../../DatePicker";

const EditDeleteSignature = ({ id, currentUser, driver }) => {
  const [removeSig, sigReq] = useRemoveDriverSignatureMutation();
  const [updateSig, { isLoading: isUpdatingSignature }] = useUpdateDriverMutation();
  const [showDialog, setShowDialog] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Use date object for hook-form. fallback to today if nothing is present.
  let initialDateValue;
  if (
    driver?.signatureW9?.DateYear &&
    driver?.signatureW9?.DateMonth &&
    driver?.signatureW9?.DateDay
  ) {
    // Construct a date in YYYY-MM-DD
    initialDateValue = moment(
      `${driver?.signatureW9?.DateYear}-${String(driver?.signatureW9?.DateMonth).padStart(2, "0")}-${String(driver?.signatureW9?.DateDay).padStart(2, "0")}`
    ).toDate();
  } else {
    initialDateValue = new Date();
  }

  const form = useForm({
    defaultValues: {
      sign: driver?.signatureW9?.sign || "",
      signatureDate: initialDateValue,
    },
  });

  const { formState: { isDirty }, setValue, getValues, reset } = form;
  const formValues = useWatch({ control: form.control });

  const handleDelete = async () => {
    const result = await removeSig(id);
    if (!result?.error) {
      sendSuccessMessage(`Signature removed successfully`);
      actions.refetch.setIsDataRefetch(true);
    } else {
      checkForError(result?.error);
    }
    setShowDialog(false);
  };

  const handleEditClick = () => {
    // Reset the form so the date-input is always up to date with underlying driver
    let newDate;
    if (
      driver?.signatureW9?.DateYear &&
      driver?.signatureW9?.DateMonth &&
      driver?.signatureW9?.DateDay
    ) {
      newDate = moment(
        `${driver?.signatureW9?.DateYear}-${String(driver?.signatureW9?.DateMonth).padStart(2, "0")}-${String(driver?.signatureW9?.DateDay).padStart(2, "0")}`
      ).toDate();
    } else {
      newDate = new Date();
    }
    reset({
      sign: driver?.signatureW9?.sign || "",
      signatureDate: newDate,
    });
    setShowUpdateModal(true);
  };

  const handleUpdate = async () => {
    const values = getValues();
    let errors = {};

    if (!values.sign || typeof values.sign !== "string" || !values.sign.trim()) {
      errors.sign = "Signature is required";
    }

    if (!values.signatureDate || !(values.signatureDate instanceof Date)) {
      errors.signatureDate = "Date is required";
    } else if (isNaN(values.signatureDate.getTime())) {
      errors.signatureDate = "Date is invalid";
    }

    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([key, msg]) => {
        form.setError(key, { type: "manual", message: msg });
      });
      return;
    }

    // signatureDate: Date instance. Extract day/month/year (all as string).
    const dateObj = values.signatureDate;
    const DateDay = String(dateObj.getDate());
    const DateMonth = String(dateObj.getMonth() + 1);
    const DateYear = String(dateObj.getFullYear());
    const data = {
      signatureW9: {
        sign: values.sign,
        DateDay,
        DateMonth,
        DateYear,
      }
    };

    const result = await updateSig({ _id: id, ...data });
    if (!result?.error) {
      sendSuccessMessage(`Signature updated successfully`);
      actions.refetch.setIsDataRefetch(true);
      setShowUpdateModal(false);
    } else {
      checkForError(result?.error);
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: '10px',
          justifyContent: 'end'
        }}
      >
        {currentUser.role === "root" && driver?.onboardingTaxFillStatus === 'completed' && (
          <>
            {/* <span
              style={{ cursor: "pointer" }}
              onClick={handleEditClick}
            >
              <EditIcon style={{ width: "20px", height: "20px" }} />
            </span> */}
            <span
              style={{ cursor: "pointer" }}
              onClick={() => setShowDialog(true)}
            >
              <DeleteIcon style={{ width: "20px", height: "20px", color: 'red' }} />
            </span>
          </>
        )}
      </div>
      {showDialog && (
        <ConfirmDialogModal
          title="Delete Driver Signature"
          bodyText="Are you sure you want to delete?"
          isOpen
          isLoading={sigReq.isLoading}
          confirmAction={handleDelete}
          onClose={() => setShowDialog(false)}
        />
      )}
      <Modal
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="modal after-loading" style={{ maxWidth: "36.875rem" }}>
          <div className="modal__header">
            <h2 className="modal__title">Update Signature</h2>
          </div>
          <div className="modal__body">
            <form
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem"
              }}
              onSubmit={e => {
                e.preventDefault();
                handleUpdate();
              }}
            >
              {/* Signature Input */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span className="fw-600" style={{ width: '100px' }}>Sign:</span>
                  <input
                    className="form__input"
                    type="text"
                    name="sign"
                    value={formValues.sign || ''}
                    onChange={e => setValue('sign', e.target.value, { shouldDirty: true })}
                    placeholder="Signature"
                    style={{ flexGrow: 1 }}
                    autoComplete="off"
                  />
                </div>
                {form.formState.errors.sign && (
                  <div style={{ color: "red", marginLeft: '100px', fontSize: '0.85rem' }}>
                    {form.formState.errors.sign.message}
                  </div>
                )}
              </div>
              {/* Signature Date Input (DateCalendar) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', height: calendarOpen ? "35vh" : "auto" }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span className="fw-600" style={{ width: '100px' }}>Date:</span>
                  <div style={{ flexGrow: 1 }}>
                    <DateCalendar
                      handleCalendarDate={() => setCalendarOpen(!calendarOpen)}
                      date={formValues.signatureDate}
                      showDate={formValues.signatureDate}
                      showDatePicker={calendarOpen}
                      onChange={date => {
                        setValue('signatureDate', date, { shouldDirty: true });
                      }}
                      submitDate={() => {
                        setCalendarOpen(false)}
                      }
                      cancelDate={() => setCalendarOpen(false)}
                      placeholder="Select Date"
                      sx={{ position: "relative" }}
                      resetDate={() => {
                        setValue('signatureDate', initialDateValue, { shouldDirty: true })
                        setCalendarOpen(false)
                      }}
                    />
                  </div>
                </div>
                {form.formState.errors.signatureDate && (
                  <div style={{ color: "red", marginLeft: '100px', fontSize: '0.85rem' }}>
                    {form.formState.errors.signatureDate.message}
                  </div>
                )}
              </div>
            </form>
          </div>
          <div className="modal__footer">
            <button
              className="btn"
              type="button"
              onClick={() => setShowUpdateModal(false)}
            >
              Cancel
            </button>
            <div style={{ position: 'relative' }}>
              <button
                className="btn btn_accent"
                onClick={handleUpdate}
                disabled={isUpdatingSignature || !isDirty || calendarOpen}
                type="button"
              >
                OK
              </button>
              {isUpdatingSignature && (
                <CircularProgress
                  size={18}
                  sx={{
                    color: '#fff',
                    position: 'absolute',
                    top: '50%',
                    left: '83%',
                    marginTop: '-8px',
                    marginLeft: '-10px',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EditDeleteSignature;